import { type ComponentType, createElement, lazy, useRef } from 'react';

export type PreloadableComponent<T extends ComponentType<unknown>> = T & {
  preload: () => Promise<T>;
};

// Global cache for lazy components to ensure same module returns same instance
const lazyComponentCache = new Map<
  string,
  PreloadableComponent<ComponentType<unknown>>
>();

/**
 * Extracts a module identifier from a dynamic import factory function.
 * Parses the import path from the factory's string representation.
 */
function extractModuleId(
  factory: () => Promise<{ default: ComponentType<unknown> }>,
): string {
  const factoryString = factory.toString();

  // Match import('./path') or import("./path") patterns
  const importMatch = factoryString.match(
    /import\s*\(\s*['"`]([^'"`]+)['"`]\s*\)/,
  );

  if (importMatch?.[1]) {
    // Normalize the path by removing relative path prefixes, file extensions, and query parameters
    const modulePath = importMatch[1]
      ?.split('?')?.[0] // Remove query parameters (like ?t=timestamp)
      ?.replace(/^\.\//, '') // Remove leading ./
      .replace(/^\.\.\//, '') // Remove leading ../
      .replace(/\.(tsx?|jsx?)$/, ''); // Remove file extensions s

    return modulePath ?? '';
  }

  // Fallback to the full factory string if we can't parse it
  return factoryString;
}

/**
 * Creates a lazily loaded React component with preloading capability.
 * Uses a global cache to ensure the same module always returns the same component instance.
 * Automatically extracts a module identifier from the import path.
 *
 * @template T The type of the React component
 * @param {() => Promise<{ default: T }>} factory Dynamic import function for the component
 * @returns {PreloadableComponent<T>} Lazily loaded component with preload method
 *
 * @example
 * // Automatically extracts 'private-tables-ContractLoansTable' from the import path
 * const LazyComponent = lazyWithPreload(() => import('./private/tables/ContractLoansTable'));
 *
 * await LazyComponent.preload(); // Optional preloading
 */
export function lazyWithPreload<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): PreloadableComponent<T> {
  // Extract module ID from the factory's import path
  const cacheKey = extractModuleId(factory);

  // Return cached component if it exists
  if (lazyComponentCache.has(cacheKey)) {
    return lazyComponentCache.get(cacheKey) as PreloadableComponent<T>;
  }

  const ReactLazyComponent = lazy<T>(factory);
  let PreloadedComponent: T | null = null;
  let factoryPromise: Promise<T> | null = null;

  const Component = function LazyWithPreload(props: Record<string, unknown>) {
    const ComponentToRender = useRef(PreloadedComponent ?? ReactLazyComponent);

    return createElement(
      ComponentToRender.current as ComponentType<Record<string, unknown>>,
      props,
    );
  };

  Component.preload = async () => {
    if (!factoryPromise) {
      factoryPromise = factory().then((module) => {
        PreloadedComponent = module.default;
        return PreloadedComponent;
      });
    }
    return factoryPromise;
  };

  const lazyComponent = Component as unknown as PreloadableComponent<T>;

  // Cache the component for future use
  lazyComponentCache.set(cacheKey, lazyComponent);

  return lazyComponent;
}

/**
 * Handles settled promises and returns an array of resolved values or `Error` instances.
 * @template T - Tuple of promises.
 * @param {T} promises - Array of promises.
 * @returns {Promise<{
 *   [K in keyof T]: T[K] extends Promise<infer R> ? R | Error : never;
 * }>} - A promise that resolves to an array where each element represents a resolved value or `Error` instance.
 */
export async function handleSettledPromises<T extends Promise<unknown>[]>(
  promises: [...T],
): Promise<{
  [K in keyof T]: T[K] extends Promise<infer R> ? R | Error : never;
}> {
  const statuses = await Promise.allSettled(promises);
  return statuses.map((settled) => {
    if (settled.status === 'fulfilled') {
      return settled.value as T[number] extends Promise<infer R> ? R : never;
    }
    return settled.reason as Error;
  }) as unknown as {
    [K in keyof T]: T[K] extends Promise<infer R> ? R | Error : never;
  };
}
